import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { categoryLabelsForTopic, userLabel } from "../labels";

function defaultCategory(topicType) {
  if (topicType === "deal") return "edit_information";
  if (topicType === "person") return "contact_details";
  return "platform_usage";
}

function initialForm(defaultDealId) {
  const topicType = defaultDealId ? "deal" : "general";

  return {
    topic_type: topicType,
    deal_id: defaultDealId ? String(defaultDealId) : "",
    target_user_id: "",
    category: defaultCategory(topicType),
    body: "",
  };
}

function fileExtension(filename) {
  const index = String(filename || "").lastIndexOf(".");
  return index >= 0 ? String(filename).slice(index).toLowerCase() : "";
}

function formatFileSize(size) {
  if (!Number.isFinite(size)) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} Ko`;
  return `${(size / 1024 / 1024).toFixed(1)} Mo`;
}

export function TicketForm({ deals = [], people = [], defaultDealId = null, attachmentConfig = null, onSubmit }) {
  const [form, setForm] = useState(() => initialForm(defaultDealId));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");

  useEffect(() => {
    if (defaultDealId) {
      setForm((current) => ({
        ...current,
        topic_type: "deal",
        deal_id: String(defaultDealId),
        target_user_id: "",
        category: defaultCategory("deal"),
      }));
    }
  }, [defaultDealId]);

  const uploadEnabled = attachmentConfig?.upload_enabled === true;
  const maxFiles = Number(attachmentConfig?.max_files || 10);
  const maxFileSize = Number(attachmentConfig?.max_file_size || 10 * 1024 * 1024);
  const allowedExtensions = Array.isArray(attachmentConfig?.allowed_extensions)
    ? attachmentConfig.allowed_extensions.map((value) => String(value).toLowerCase())
    : [".pdf", ".jpg", ".jpeg", ".png"];
  const accept = attachmentConfig?.accept || allowedExtensions.join(",");

  const selectAttachments = (event) => {
    setAttachmentError("");
    const incoming = Array.from(event.currentTarget.files || []);
    event.currentTarget.value = "";

    const uniqueIncoming = incoming.filter((file) =>
      !attachments.some((current) =>
        current.name === file.name && current.size === file.size && current.lastModified === file.lastModified
      )
    );

    if (attachments.length + uniqueIncoming.length > maxFiles) {
      setAttachmentError(`Vous pouvez joindre au maximum ${maxFiles} documents.`);
      return;
    }

    const invalid = uniqueIncoming.find((file) =>
      file.size <= 0 || file.size > maxFileSize || !allowedExtensions.includes(fileExtension(file.name))
    );

    if (invalid) {
      if (invalid.size > maxFileSize) {
        setAttachmentError(`${invalid.name} dépasse la taille maximale de ${Math.round(maxFileSize / 1024 / 1024)} Mo.`);
      } else {
        setAttachmentError(`${invalid.name} n'est pas dans un format autorisé.`);
      }
      return;
    }

    setAttachments((current) => [...current, ...uniqueIncoming]);
  };

  const removeAttachment = (index) => {
    setAttachments((current) => current.filter((_file, currentIndex) => currentIndex !== index));
    setAttachmentError("");
  };

  const setField = (field, value) => {
    setForm((current) => {
      if (field === "topic_type") {
        return {
          ...current,
          topic_type: value,
          deal_id: value === "deal" ? current.deal_id : "",
          target_user_id: value === "person" ? current.target_user_id : "",
          category: defaultCategory(value),
        };
      }

      return { ...current, [field]: value };
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (form.topic_type === "deal" && !form.deal_id) {
      setError("Sélectionnez le dossier concerné.");
      return;
    }

    if (form.topic_type === "person" && !form.target_user_id) {
      setError("Sélectionnez la personne concernée.");
      return;
    }

    setSending(true);

    try {
      await onSubmit({
        ...form,
        deal_id: form.topic_type === "deal" ? Number(form.deal_id) : null,
        target_user_id: form.topic_type === "person" ? Number(form.target_user_id) : null,
        body: form.body.trim(),
        attachments: uploadEnabled ? attachments : [],
      });
      setForm(initialForm(defaultDealId));
      setAttachments([]);
      setAttachmentError("");
    } catch (submitError) {
      setError(submitError.message || "La demande n’a pas pu être envoyée.");
    } finally {
      setSending(false);
    }
  };

  const categoryLabels = categoryLabelsForTopic(form.topic_type);

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <FormControl fullWidth size="small">
          <InputLabel id="support-topic-type-label">Votre demande concerne</InputLabel>
          <Select
            labelId="support-topic-type-label"
            label="Votre demande concerne"
            value={form.topic_type}
            onChange={(event) => setField("topic_type", event.target.value)}
            disabled={sending}
          >
            <MenuItem dense value="general">Une question générale</MenuItem>
            <MenuItem dense value="deal">Un dossier en particulier</MenuItem>
            <MenuItem dense value="person">Une personne en particulier</MenuItem>
          </Select>
        </FormControl>

        {form.topic_type === "deal" ? (
          <FormControl fullWidth required size="small">
            <InputLabel id="support-deal-label">Dossier</InputLabel>
            <Select
              labelId="support-deal-label"
              label="Dossier"
              value={form.deal_id}
              onChange={(event) => setField("deal_id", event.target.value)}
              disabled={sending}
            >
              {deals.map((deal) => (
                <MenuItem dense key={deal.id} value={String(deal.id)}>
                  {deal.name || "Dossier sans nom"}
                  {deal.role ? ` — ${deal.role}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        {form.topic_type === "person" ? (
          <FormControl fullWidth required size="small">
            <InputLabel id="support-person-label">Personne</InputLabel>
            <Select
              labelId="support-person-label"
              label="Personne"
              value={form.target_user_id}
              onChange={(event) => setField("target_user_id", event.target.value)}
              disabled={sending}
            >
              {people.map((person) => (
                <MenuItem dense key={person.id} value={String(person.id)}>
                  {userLabel(person)}{person.role ? ` — ${person.role}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <FormControl fullWidth size="small">
          <InputLabel id="support-category-label">Sous-catégorie</InputLabel>
          <Select
            labelId="support-category-label"
            label="Sous-catégorie"
            value={form.category}
            onChange={(event) => setField("category", event.target.value)}
            disabled={sending}
          >
            {Object.entries(categoryLabels).map(([value, label]) => (
              <MenuItem dense key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Votre message"
          value={form.body}
          onChange={(event) => setField("body", event.currentTarget.value)}
          inputProps={{ minLength: 1, maxLength: 12000 }}
          multiline
          minRows={6}
          required
          fullWidth
          disabled={sending}
        />

        {uploadEnabled ? (
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Button component="label" variant="outlined" size="small" disabled={sending || attachments.length >= maxFiles}>
                  Ajouter des documents
                  <input
                    hidden
                    type="file"
                    multiple
                    accept={accept}
                    onChange={selectAttachments}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {attachments.length}/{maxFiles} · {Math.round(maxFileSize / 1024 / 1024)} Mo max par fichier · {allowedExtensions.join(", ")}
                </Typography>
              </Box>

              {attachmentError ? <Alert severity="error">{attachmentError}</Alert> : null}

              {attachments.length ? (
                <Stack spacing={0.75}>
                  {attachments.map((file, index) => (
                    <Box
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, bgcolor: "grey.50", borderRadius: 1, px: 1, py: 0.75 }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file.name} · {formatFileSize(file.size)}
                      </Typography>
                      <Button type="button" size="small" color="inherit" disabled={sending} onClick={() => removeAttachment(index)}>
                        Retirer
                      </Button>
                    </Box>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={sending || !form.body.trim()}
          >
            {sending ? "Envoi…" : "Envoyer au support"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
